import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckCircle } from "@coreui/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import type { ApiError } from "../../../api/client";
import { useFailure } from "../../../context/useFailure";
import { useCartStore, type ExtendLine } from "../../../state/cartStore";
import {
  MY_SUBSCRIPTIONS_QUERY_KEY,
  useExtendYear,
  useExtensionPreview,
  useExtensionStatus,
  type ExtendSessionResponse,
  type ExtensionPreviewDto,
} from "../../../api/hooks/useStrategySubscriptions";
import { BILLING_QUERY_KEY } from "../../../api/hooks/useBilling";
import { offersForLine, usableOffers, useUsableOffers } from "../../../api/hooks/useOffers";
import { formatPriceUsd } from "../../../lib/money";
import { formatDateShort, formatDayMonth } from "../../../lib/dates";
import { extensionRenewalUsd } from "../../../lib/extension-figures";
import { stripePromise } from "../../../lib/stripe";
import { routePaths } from "../../../routes/route-paths";
import { useTradingStrategies } from "../../../api/hooks/useTradingStrategies";
import { StrategySideLabel } from "../components/StrategySideLabel";
import { ExtendDiscountWarning } from "../components/ExtendDiscountWarning";

// CStepper's imperative API, as the purchase checkout uses it.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

// How long Done waits for the payment to be asked for before saying it's taking a while. The message
// processor takes it a few hops after the card is saved, and longer from a cold start.
const PAYMENT_WAIT_MS = 120_000;

interface Booking {
  line: ExtendLine;
  offerName: string | null;
  renewalUsd: number;
}

/** The checkout for an extension: its own review and outcome around the usual card form. */
export function ExtendCheckoutWizard() {
  const navigate = useNavigate();
  const cartExtension = useCartStore((s) => s.extension);
  const clearCart = useCartStore((s) => s.clear);
  const startExtension = useCartStore((s) => s.startExtension);
  const setCheckoutLocked = useCartStore((s) => s.setCheckoutLocked);
  const offers = usableOffers(useUsableOffers().data);
  const { data: strategies } = useTradingStrategies();
  const openExtendSession = useExtendYear();
  const queryClient = useQueryClient();
  const { showFailure, clearFailure } = useFailure();
  const stepperRef = useRef<CStepperRef>(null);

  // Kept from when the card session opens: paying empties the cart, and Done still has to say what
  // happened.
  const [booking, setBooking] = useState<Booking | null>(null);
  const [session, setSession] = useState<ExtendSessionResponse | null>(null);
  const [openingSession, setOpeningSession] = useState(false);
  const [paid, setPaid] = useState(false);
  const [takingLong, setTakingLong] = useState(false);

  const line = booking?.line ?? cartExtension;
  const preview = useExtensionPreview(
    paid ? null : (line?.strategyId ?? null),
    line?.offerId ?? null,
  );
  const status = useExtensionStatus(
    line?.strategyId ?? "",
    paid ? (session?.sessionId ?? null) : null,
    takingLong,
  );
  const outcome = status.data?.status;

  const offer =
    line === null
      ? null
      : (offersForLine(offers, line.strategyId, "Yearly").find((o) => o.id === line.offerId) ?? null);
  const checkoutStarted = session !== null;

  // Freeze the cart for exactly as long as a session is open, as the purchase checkout does.
  useEffect(() => {
    setCheckoutLocked(checkoutStarted);
    return () => setCheckoutLocked(false);
  }, [checkoutStarted, setCheckoutLocked]);

  useEffect(() => {
    if (!paid || takingLong || (outcome !== undefined && outcome !== "Waiting")) {
      return;
    }

    const timer = setTimeout(() => setTakingLong(true), PAYMENT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [paid, takingLong, outcome]);

  // Renews moves and the next payment changes once the new year is Stripe's.
  useEffect(() => {
    if (outcome === "Extended") {
      queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    }
  }, [outcome, queryClient]);

  const handleProceedToPayment = async () => {
    if (line === null || !preview.data) {
      return;
    }

    setOpeningSession(true);
    clearFailure();
    try {
      const opened = await openExtendSession.mutateAsync({
        strategyId: line.strategyId,
        offerId: line.offerId,
      });
      setBooking({
        line,
        offerName: offer?.publicName ?? null,
        renewalUsd: extensionRenewalUsd(preview.data, offer),
      });
      setSession(opened);
      stepperRef.current?.next();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "We couldn't start your extension. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setOpeningSession(false);
    }
  };

  // Leaving Payment abandons the session, which unlocks the cart. Returning opens a fresh one.
  const handleBackFromPayment = () => {
    setSession(null);
    setBooking(null);
    stepperRef.current?.prev();
  };

  const handlePaymentComplete = useCallback(() => {
    setPaid(true);
    clearCart();
    stepperRef.current?.next();
  }, [clearCart, setPaid]);

  // Back to the cart with the same extension and offer, for another card.
  const tryAgain = () => {
    if (booking === null) {
      return;
    }

    setCheckoutLocked(false);
    startExtension(booking.line);
    navigate(routePaths.cart);
  };

  const embeddedOptions = useMemo(
    () => (session ? { clientSecret: session.clientSecret, onComplete: handlePaymentComplete } : null),
    [session, handlePaymentComplete]
  );

  // Below every hook, as in the purchase checkout: returning earlier changes the hook count.
  if (line === null) {
    return (
      <div>
        <CAlert color="info">
          Your cart is empty. <Link to={routePaths.strategies}>Browse strategies</Link>.
        </CAlert>
      </div>
    );
  }

  const name = line.name;
  const direction = strategies?.find((s) => s.id === line.strategyId)?.direction;
  const steps = [
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review your extension</h4>
          </CCardHeader>
          <CCardBody>
            <p className="mb-3">
              <strong>{name}</strong>
              <StrategySideLabel direction={direction} className="small ms-2 align-middle" />
              {offer && <span className="text-body-secondary"> · {offer.publicName}</span>}
            </p>

            {preview.isLoading && (
              <div className="text-center py-4 text-body-secondary">
                <CSpinner size="sm" className="me-2" />
                Asking Stripe what extending comes to…
              </div>
            )}
            {preview.error && <CAlert color="warning">{preview.error.message}</CAlert>}
            {preview.data && (
              <ReviewFigures name={name} preview={preview.data} renewalUsd={extensionRenewalUsd(preview.data, offer)} />
            )}
            {preview.data && (
              <ExtendDiscountWarning
                strategyName={name}
                preview={preview.data}
                chosenOfferId={line.offerId}
                offers={offers}
              />
            )}

            <div className="d-flex justify-content-between mt-4">
              <CButton color="secondary" variant="outline" onClick={() => navigate(routePaths.cart)}>
                Back to cart
              </CButton>
              <CButton
                color="primary"
                onClick={handleProceedToPayment}
                disabled={!preview.data || openingSession}
              >
                {openingSession && <CSpinner size="sm" className="me-2" />}
                Next: Payment
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Payment",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Payment</h4>
          </CCardHeader>
          <CCardBody>
            {session && (
              <p className="text-body-secondary">
                {session.dueTodayUsd > 0 ? (
                  <>
                    Due today: <strong>{formatPriceUsd(session.dueTodayUsd)}</strong>, taken as soon as
                    your card is saved.
                  </>
                ) : (
                  <>
                    Due today: <strong>{formatPriceUsd(0)}</strong>. Your credit covers the new year.
                  </>
                )}
              </p>
            )}
            <p className="text-body-secondary">
              Your cart is locked while this payment is open. Use <strong>Back</strong> if you need to
              change it.
            </p>
            {embeddedOptions ? (
              <div className="mb-3">
                <EmbeddedCheckoutProvider stripe={stripePromise} options={embeddedOptions}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : (
              <div className="text-center py-5 text-body-secondary">
                <CSpinner size="sm" className="me-2" />
                Preparing secure checkout…
              </div>
            )}
            <div className="d-flex justify-content-start">
              <CButton color="secondary" variant="outline" onClick={handleBackFromPayment}>
                Back
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Done",
      content:
        session && booking ? (
          <ExtendDone
            name={name}
            strategyId={line.strategyId}
            session={session}
            renewalUsd={booking.renewalUsd}
            outcome={outcome}
            runsTo={status.data?.runsTo ?? null}
            paymentPageUrl={status.data?.paymentPageUrl ?? null}
            takingLong={takingLong}
            onTryAgain={tryAgain}
          />
        ) : null,
    },
  ];

  return (
    <>
      <h2>Checkout</h2>
      <p className="text-body-secondary">Review your extension and save your card.</p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} />
    </>
  );
}

function ReviewFigures({
  name,
  preview,
  renewalUsd,
}: {
  name: string;
  preview: ExtensionPreviewDto;
  renewalUsd: number;
}) {
  const newEnd = formatDateShort(preview.newPeriodEnd);
  const renewalDay = formatDayMonth(preview.newPeriodEnd);

  return (
    <>
      <div className="border rounded px-3">
        <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
          <span>
            New year
            <span className="d-block text-body-secondary small">To {newEnd}</span>
          </span>
          <span className="text-nowrap">{formatPriceUsd(preview.newYearUsd)}</span>
        </div>
        <div className="d-flex justify-content-between gap-3 py-2 border-bottom text-success">
          <span>Time you haven't used</span>
          <span className="text-nowrap">−{formatPriceUsd(preview.unusedTimeCreditUsd)}</span>
        </div>
        <div className="d-flex justify-content-between gap-3 py-2 border-bottom fw-bold">
          <span>Due today</span>
          <span className="text-nowrap">{formatPriceUsd(preview.dueTodayUsd)}</span>
        </div>
        {preview.leftOnAccountUsd > 0 && (
          <div className="d-flex justify-content-between gap-3 py-2 border-bottom">
            <span>Left as credit on your account</span>
            <span className="text-nowrap text-success fw-semibold">
              {formatPriceUsd(preview.leftOnAccountUsd)}
            </span>
          </div>
        )}
        <div className="d-flex justify-content-between gap-3 py-2">
          <span>Every {renewalDay} after</span>
          <span className="text-nowrap">{formatPriceUsd(renewalUsd)}</span>
        </div>
      </div>

      <p className="text-body-secondary small mt-3 mb-0">
        {preview.dueTodayUsd > 0 ? (
          <>
            {formatPriceUsd(preview.dueTodayUsd)} is taken as soon as your card is saved, for a year of{" "}
            {name} to {newEnd}.
          </>
        ) : (
          <>Nothing is taken today. Your year of {name} runs to {newEnd}.</>
        )}{" "}
        {preview.leftOnAccountUsd > 0 && (
          <>
            {formatPriceUsd(preview.leftOnAccountUsd)} is left as credit on your account. It comes off
            your next payments first, for any strategy, and isn't paid out.{" "}
          </>
        )}
        It then renews at {formatPriceUsd(renewalUsd)} every {renewalDay} until you end it. You can end
        it from My strategies: a year isn't refunded part-way, and access runs to the end of it. If
        your payment doesn't go through, nothing changes. Your access doesn't change.
      </p>
    </>
  );
}

interface ExtendDoneProps {
  name: string;
  strategyId: string;
  session: ExtendSessionResponse;
  renewalUsd: number;
  outcome: string | undefined;
  runsTo: string | null;
  paymentPageUrl: string | null;
  takingLong: boolean;
  onTryAgain: () => void;
}

/** Done for every way an extension can end, as Stripe reports it. */
function ExtendDone({
  name,
  strategyId,
  session,
  renewalUsd,
  outcome,
  runsTo,
  paymentPageUrl,
  takingLong,
  onTryAgain,
}: ExtendDoneProps) {
  const due = formatPriceUsd(session.dueTodayUsd);
  const viewStrategy = (
    <Link to={`/subscriptions/${strategyId}`} className="btn btn-primary">
      View {name}
    </Link>
  );
  const viewStrategyQuietly = (
    <Link to={`/subscriptions/${strategyId}`} className="btn btn-outline-secondary">
      View {name}
    </Link>
  );

  if (outcome === "Extended") {
    return (
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">All set</h4>
        </CCardHeader>
        <CCardBody>
          <div className="text-center py-3 mb-2">
            <CIcon icon={cilCheckCircle} size="3xl" className="text-success mb-3" />
            <h3 className="mb-2">Your year is extended</h3>
            <p className="text-body-secondary mx-auto" style={{ maxWidth: 480 }}>
              {name} now runs to {runsTo ? formatDateShort(runsTo) : formatDateShort(session.newPeriodEnd)}.{" "}
              {session.dueTodayUsd > 0
                ? `${due} was taken today, and Stripe emails your receipt.`
                : "Nothing was taken today."}{" "}
              It renews at {formatPriceUsd(renewalUsd)} every {formatDayMonth(session.newPeriodEnd)}. Your
              access doesn't change.
            </p>
            {session.leftOnAccountUsd > 0 && (
              <p className="mx-auto" style={{ maxWidth: 480 }}>
                <strong>{formatPriceUsd(session.leftOnAccountUsd)} is left as credit on your account.</strong>{" "}
                It comes off your next payments first, for any strategy, and isn't paid out. You'll see it
                on Billing.
              </p>
            )}
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {viewStrategy}
            <Link to={routePaths.myStrategies} className="btn btn-outline-secondary">
              My strategies
            </Link>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  if (outcome === "PaymentDeclined") {
    return (
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">Your payment didn't go through</h4>
        </CCardHeader>
        <CCardBody>
          <div className="tinted-warning rounded p-3 mb-3">
            Your bank declined the {due}, so <strong>nothing has changed</strong>. {name} still runs
            {runsTo ? ` to ${formatDateShort(runsTo)}` : ""} and your access carries on. You can try again
            with another card.
          </div>
          <div className="d-flex flex-wrap gap-2">
            <CButton color="primary" onClick={onTryAgain}>
              Try again
            </CButton>
            {viewStrategyQuietly}
          </div>
        </CCardBody>
      </CCard>
    );
  }

  if (outcome === "NeedsAuthentication") {
    return (
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">Your bank wants to confirm this payment</h4>
        </CCardHeader>
        <CCardBody>
          <div className="tinted-warning rounded p-3 mb-3">
            Your bank asked to confirm the {due} before it's taken, so <strong>nothing has changed yet</strong>.
            Confirm it on Stripe's payment page and your year is extended straight away. The payment stays
            open for 23 hours.
          </div>
          <div className="d-flex flex-wrap gap-2">
            {paymentPageUrl && (
              <a href={paymentPageUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                Confirm with your bank
              </a>
            )}
            <CButton color="secondary" variant="outline" onClick={onTryAgain}>
              Try another card
            </CButton>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  if (outcome === "Ended") {
    return (
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">{name} wasn't extended</h4>
        </CCardHeader>
        <CCardBody>
          <div className="tinted-info rounded p-3 mb-3">
            {name} was ended before your payment was taken, so it wasn't extended and{" "}
            <strong>nothing was charged</strong>. Your card is saved. To keep {name}, resume it from its
            page and extend it again.
          </div>
          {viewStrategy}
        </CCardBody>
      </CCard>
    );
  }

  if (takingLong) {
    return (
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">This is taking longer than usual</h4>
        </CCardHeader>
        <CCardBody>
          <div className="tinted-warning rounded p-3 mb-3">
            Your card is saved, but your payment hasn't been taken yet. <strong>Nothing has been charged so
            far</strong>, and it will finish on its own. Look at {name}'s page again in a few minutes, and
            contact support if it hasn't changed by tomorrow.
          </div>
          {viewStrategy}
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard>
      <CCardHeader>
        <h4 className="mb-0">Taking your payment</h4>
      </CCardHeader>
      <CCardBody>
        <div className="text-center py-4 text-body-secondary">
          <CSpinner size="sm" className="me-2" />
          {session.dueTodayUsd > 0
            ? `Your card is saved. Taking your ${due} payment, which usually takes a few seconds.`
            : "Your card is saved. Extending your year, which usually takes a few seconds."}
        </div>
      </CCardBody>
    </CCard>
  );
}
